import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Icon,
  Link,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-core';
import arraySort from 'array-sort';
import {
  ContentRequest,
  ContentType,
  PublicationSearchResultItem,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useContext, useEffect, useState } from 'react';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { displayDate } from '../../api/UtilsApi';
import {
  ContentRequestTagKey,
  contentRequestNeedsBarRaiser,
  getDefaultFilterSelection,
} from '../../api/UtilsApi';
import constants from '../../common/constants';
import '../../resources/styles/scss/components/content-request/content-request-creation.scss';
import InfoLink from '../common/InfoLink';
import UserIdAutosuggest from '../common/UserIdAutosuggest';
import BrowserHistory from '../utils/BrowserHistory';
import { decodeSearchtextString } from '../utils/Search';
import PublicationLink from '../publication/PublicationLink';
import {
  convertEntities,
  getTextWithEntityPlaceholders,
  TiptapOutputType,
} from '@amzn/awscat-editor';

import {
  AWSServiceOptions,
  ContentTypeOptions,
  TechDomainOptions,
  WorkloadOptions,
} from '../utils/SelectableOptions';
import EditorWrapper from '../../editor/EditorWrapper';
import { TiptapMenuType } from '../../editor/utils';
import { AppContext, withAppContext } from '../../contexts/AppContext';

const DESCRIPTION_MAX_LENGTH = 400;
const DEFAULT_CONTENT_TYPE: SelectProps.Option = getDefaultFilterSelection(ContentType.Pattern);

enum SearchResultType {
  Suggestion = 'Suggestion',
  ApgLibraryContent = 'APG Library content',
}

interface SelectedTags {
  Technology?: SelectProps.Option;
  TechDomain?: SelectProps.Option;
  WorkLoad?: SelectProps.Option;
  AwsSServices?: SelectProps.Option;
}

interface DuplicateContent extends PublicationSearchResultItem {
  searchResultType: SearchResultType;
}

interface DuplicateContentRequest extends ContentRequest {
  contentName: string;
  searchResultType: SearchResultType;
}

let duplicateSearchTimer: NodeJS.Timeout | number | undefined;
const DUPLICATE_SEARCH_TIMER_DELAY = 2000;
const DEFAULT_DUPLICATE_CONTENT_TO_SHOW = 5;
const NUMBER_OF_DUPLICATE_CONTENT_TO_ADD = 10;

const ContentRequestCreationHeader: React.FunctionComponent = () => (
  <>
    <Box variant="p" fontSize="heading-xl" padding={{ bottom: 'n' }}>
      {constants.CONTENT_REQUEST_CREATION_HEADER} <InfoLink topic={'content-request-creation'} />
    </Box>
    <Box variant="p" padding={{ bottom: 's', top: 'n' }}>
      {constants.CONTENT_REQUEST_CREATION_HEADER_DESC}
    </Box>
  </>
);

let mplServiceClient: IMplServiceClient;

const ContentRequestCreation: React.FunctionComponent = () => {
  const [title, setTitle] = React.useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [contentType, setContentType] = useState<SelectProps.Option>(DEFAULT_CONTENT_TYPE);
  const [selectedTags, setSelectedTags] = useState<SelectedTags>({});
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [titleErrorText, setTitleErrorText] = useState<string>('');
  const [descriptionErrorText, setDescriptionErrorText] = useState<string>('');
  const [isCreatingInProgress, setIsInProgress] = useState<boolean>(false);
  const [creationErrorMessage, setCreationError] = useState<string>('');
  const [isDuplicateSearchState, setDuplicateSearchState] = useState<boolean>(false);
  const [duplicateContent, setDuplicateContents] = useState<
    Array<DuplicateContent | DuplicateContentRequest>
  >([]);
  const [numberOfDuplicateToShow, setNumberOfDuplicateToShow] = useState<number>(
    DEFAULT_DUPLICATE_CONTENT_TO_SHOW,
  );
  const [allContentRequests, setAllContentRequests] = useState<ContentRequest[]>([]);
  const [requireCode, setHasCode] = useState<boolean>(false);
  const [isAssigneeDisabled, setAssigneeDisabled] = useState<boolean>(false);

  const appContext = useContext(AppContext);
  const { entities } = appContext;

  const columnDefinitions = [
    {
      // @ts-expect-error TS7006 - strict migration
      cell: (item) =>
        item.searchResultType === SearchResultType.Suggestion ? (
          <Box padding={{ left: 'xs' }}>
            <Link target="_blank" external href={`/review-content-request/${item.requestId}`}>
              {`${convertEntities(item.contentName, entities) || '-'} `}
            </Link>
          </Box>
        ) : (
          <PublicationLink
            contentName={item.contentName}
            contentId={item.contentId}
            target={'_blank'}
            workflowId={item.publicationWorkflowId}
          />
        ),
      header: 'Title',
      id: 'title',
    },
    {
      // @ts-expect-error TS7006 - strict migration
      cell: (item) => (
        <Box variant="p" color="text-body-secondary">{`${item.contentType || '-'}`}</Box>
      ),
      header: 'Content type',
      id: 'contentType',
    },
    {
      // @ts-expect-error TS7006 - strict migration
      cell: (item) => (
        <Box variant="p" color="text-body-secondary">{`${item.searchResultType || '-'}`}</Box>
      ),
      header: 'State',
      id: 'state',
    },
    {
      // @ts-expect-error TS7006 - strict migration
      cell: (item) => (
        <Box
          variant="p"
          color="text-body-secondary"
        >{`${displayDate(item.publishDate) || '-'}`}</Box>
      ),
      header: 'Last update',
      id: 'lastUpdate',
    },
  ];

  useEffect(() => {
    const fetchAllContentRequests = async () => {
      const listsOfContentRequest = await mplServiceClient.listContentRequests();
      setAllContentRequests(listsOfContentRequest.contentRequests);
    };
    mplServiceClient = MplServiceClientFactory.create();
    fetchAllContentRequests();
  }, []);

  useEffect(() => {
    if (contentType.label !== ContentType.Pattern) {
      setHasCode(false);
    }
  }, [contentType]);

  const handleTitleChange = async (value: TiptapOutputType) => {
    const valueText = getTextWithEntityPlaceholders(value);
    setDuplicateSearchState(true);
    setNumberOfDuplicateToShow(DEFAULT_DUPLICATE_CONTENT_TO_SHOW);
    setTitle(valueText);
    setTitleErrorText('');
    clearTimeout(duplicateSearchTimer);
    duplicateSearchTimer = setTimeout(async () => {
      // TODO: validate search result after processing entities for cloudsearch index
      const contentSearchResults = await mplServiceClient.searchPublicationsByField(
        decodeSearchtextString(valueText),
        'name',
      );
      const contentSearchResultItems = contentSearchResults.publications.map((searchResult) => ({
        ...searchResult,
        searchResultType: SearchResultType.ApgLibraryContent,
      }));
      const filteredContentRequests = detectDuplicatedSuggestions(
        convertEntities(valueText, entities),
      );
      const duplicateCandidates = [...filteredContentRequests, ...contentSearchResultItems];
      setDuplicateContents(duplicateCandidates);
      setDuplicateSearchState(false);
    }, DUPLICATE_SEARCH_TIMER_DELAY);
  };

  const detectDuplicatedSuggestions = (keyword: string): DuplicateContentRequest[] => {
    const filteredContentRequests: DuplicateContentRequest[] = [];
    const keywordToSearch = keyword.toLowerCase().trim();
    allContentRequests.forEach((contentRequest) => {
      const matchTitle = convertEntities(contentRequest.contentRequestData.contentName, entities)
        .toLowerCase()
        .includes(keywordToSearch);
      const matchDescription = convertEntities(
        contentRequest.contentRequestData.description,
        entities,
      )
        .toLowerCase()
        .includes(keywordToSearch);
      if (matchTitle || matchDescription) {
        filteredContentRequests.push({
          ...contentRequest,
          contentName: contentRequest.contentRequestData.contentName,
          searchResultType: SearchResultType.Suggestion,
        });
      }
    });
    return arraySort(filteredContentRequests, 'totalVotes', { reverse: true });
  };

  const handleDescriptionChange = (value: TiptapOutputType) => {
    const valueText = getTextWithEntityPlaceholders(value);
    setDescription(valueText);
    setDescriptionErrorText('');
  };

  const handleContentTypeChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    setContentType(event.detail.selectedOption);
  };

  const handleTagsChange = (
    tagKey: ContentRequestTagKey,
    event: NonCancelableCustomEvent<SelectProps.ChangeDetail>,
  ) => {
    const tags = { ...selectedTags };
    // @ts-expect-error TS2551 - strict migration
    tags[tagKey] = event.detail.selectedOption;
    setSelectedTags(tags);
    const requestNeedsBarRaiser = Object.values(ContentRequestTagKey).some(
      // @ts-expect-error TS2551 - strict migration
      (key) => tags[key] && contentRequestNeedsBarRaiser(key, tags[key].id),
    );
    setAssigneeDisabled(requestNeedsBarRaiser);
    if (requestNeedsBarRaiser) {
      setAssigneeId('');
    }
  };

  const handleAssigneeSelected = (
    selectedUserId: string,
    selectedUserName: string,
    validationError: any,
  ) => {
    if (!validationError) {
      setAssigneeId(selectedUserId);
    } else if (assigneeId) {
      setAssigneeId('');
    }
  };

  const validateInput = (newTitle: string, newDescription: string): boolean => {
    const isTitleValid = !!newTitle && newTitle.length > 0;
    const isDescriptionValid =
      !!newDescription &&
      newDescription.length > 0 &&
      newDescription.length < DESCRIPTION_MAX_LENGTH;
    if (isTitleValid) {
      setTitleErrorText('');
    } else {
      setTitleErrorText(constants.MISSING_REQUIRED_FIELD);
    }
    if (isDescriptionValid) {
      setDescriptionErrorText('');
    } else {
      setDescriptionErrorText(constants.MISSING_REQUIRED_FIELD);
    }
    return isTitleValid && isDescriptionValid;
  };

  const renderDescriptionMessage = () => {
    const charactersRemaining = DESCRIPTION_MAX_LENGTH - description.length;
    return description.length > DESCRIPTION_MAX_LENGTH ? (
      <StatusIndicator type="warning" colorOverride="red">
        <Box fontSize="body-s" display="inline" color="text-status-error">
          {`Must be less than ${DESCRIPTION_MAX_LENGTH} characters`}
        </Box>
      </StatusIndicator>
    ) : (
      <Box color="text-body-secondary" fontSize="body-s">
        {`${charactersRemaining} character${charactersRemaining > 1 ? 's' : ''} remaining`}
      </Box>
    );
  };

  const renderContentTypeSelector = () => {
    return (
      <div className="cr-creation-max-width-container">
        <FormField
          label={constants.CONTENT_REQUEST_CREATION_CONTENT_TYPE_LABEL}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <ColumnLayout columns={2}>
            <div data-awsui-column-layout-root="true">
              <Select
                options={ContentTypeOptions}
                selectedOption={contentType}
                onChange={handleContentTypeChange}
                ariaRequired
              />
            </div>
          </ColumnLayout>
        </FormField>
      </div>
    );
  };

  const renderTagSelections = () => {
    /* tslint:disable:jsx-no-lambda */
    return (
      <div className="cr-creation-max-width-container">
        <ColumnLayout columns={2}>
          <FormField
            label={constants.CONTENT_REQUEST_CREATION_MAIN_TECHNOLOGY_LABEL}
            description={constants.CONTENT_REQUEST_CREATION_MAIN_TECHNOLOGY_DESCRIPTION}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Select
              // @ts-expect-error TS2551 - strict migration
              selectedOption={selectedTags[ContentRequestTagKey.MainTechnology]}
              options={TechDomainOptions}
              placeholder={`Choose ${constants.CONTENT_REQUEST_CREATION_MAIN_TECHNOLOGY_LABEL}`}
              onChange={(e) => handleTagsChange(ContentRequestTagKey.MainTechnology, e)}
              ariaRequired
            />
          </FormField>
          <FormField
            label={constants.CONTENT_REQUEST_CREATION_ADDITIONAL_TECHNOLOGY_LABEL}
            description={constants.CONTENT_REQUEST_CREATION_ADDITIONAL_TECHNOLOGY_DESCRIPTION}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Select
              // @ts-expect-error TS2551 - strict migration
              selectedOption={selectedTags[ContentRequestTagKey.TechDomain]}
              options={TechDomainOptions}
              placeholder={`Choose ${constants.CONTENT_REQUEST_CREATION_ADDITIONAL_TECHNOLOGY_LABEL}`}
              onChange={(e) => handleTagsChange(ContentRequestTagKey.TechDomain, e)}
              ariaRequired
            />
          </FormField>
          <FormField
            label={
              <>
                {ContentRequestTagKey.WorkLoad} <InfoLink topic={'workload'} />
              </>
            }
            description={constants.CONTENT_REQUEST_CREATION_WORKLOAD_DESCRIPTION}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Select
              // @ts-expect-error TS2551 - strict migration
              selectedOption={selectedTags[ContentRequestTagKey.WorkLoad]}
              options={WorkloadOptions}
              placeholder={`Choose ${ContentRequestTagKey.WorkLoad}`}
              onChange={(e) => handleTagsChange(ContentRequestTagKey.WorkLoad, e)}
              ariaRequired
            />
          </FormField>
          <FormField
            label={ContentRequestTagKey.AwsServices}
            description={constants.CONTENT_REQUEST_CREATION_AWS_SERVICES_DESCRIPTION}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Select
              // @ts-expect-error TS2551 - strict migration
              selectedOption={selectedTags[ContentRequestTagKey.AwsServices]}
              options={AWSServiceOptions}
              placeholder={`Choose ${ContentRequestTagKey.AwsServices}`}
              onChange={(e) => handleTagsChange(ContentRequestTagKey.AwsServices, e)}
              ariaRequired
            />
          </FormField>
        </ColumnLayout>
      </div>
    );
  };

  const renderActionButton = () => {
    const submitButton = (
      <Button variant="primary" onClick={handleSuggestionSubmit} loading={isCreatingInProgress}>
        {constants.CONTENT_REQUEST_CREATION_CREATE_SUGGESTION_BUTTON_TEXT}
      </Button>
    );

    return (
      <Box float="right">
        <Button variant="link" onClick={cancelSuggestionCreation}>
          Cancel
        </Button>
        &nbsp;
        {submitButton}
      </Box>
    );
  };

  const renderCreationError = () => {
    if (!creationErrorMessage) {
      return null;
    }
    return (
      <StatusIndicator type="warning" colorOverride="red">
        <Box fontSize="body-s" display="inline" color="text-status-error">
          {creationErrorMessage}
        </Box>
      </StatusIndicator>
    );
  };

  const renderDuplicates = () => {
    if (!title) {
      return null;
    }
    const duplicateContentToShow = duplicateContent.slice(0, numberOfDuplicateToShow);
    if (isDuplicateSearchState) {
      return (
        <Box variant="div" padding={{ left: 'm', bottom: 's' }}>
          <Spinner />
          <Box
            variant="p"
            padding={{ left: 'xs' }}
            display="inline"
            fontSize="body-s"
            color="text-body-secondary"
          >
            {constants.CONTENT_REQUEST_CREATION_DUPLICATE_SEARCH_MESSAGE}
          </Box>
        </Box>
      );
    }
    if (!duplicateContentToShow.length) {
      return (
        <Box variant="div" padding={{ left: 'm', bottom: 'm' }}>
          <Icon variant="success" name="status-positive" />
          <Box variant="p" fontSize="body-s" display="inline" padding={{ left: 'xs' }}>
            {constants.CONTENT_REQUEST_CREATION_NO_DUPLICATE_CONTENT}
          </Box>
        </Box>
      );
    }
    const duplicateContentInfo = `
            We found ${duplicateContent.length} article${duplicateContent.length > 1 ? 's' : ''}
            that ${duplicateContent.length > 1 ? 'are' : 'is'} similar to your title
        `;

    const numberOfDuplicateToAdd =
      duplicateContent.length - numberOfDuplicateToShow >= NUMBER_OF_DUPLICATE_CONTENT_TO_ADD
        ? NUMBER_OF_DUPLICATE_CONTENT_TO_ADD
        : duplicateContent.length - numberOfDuplicateToShow;
    const showMoreDuplicate =
      numberOfDuplicateToShow < duplicateContent.length ? (
        <div className="cr-creation-show-more-duplicate" onClick={showMoreDuplicateContent}>
          <Box variant="div" padding={{ left: 'm', top: 's', bottom: 's' }}>
            <Icon variant="success" name="treeview-expand" />
            <Box
              variant="p"
              padding={{ left: 'xs' }}
              display="inline"
              fontSize="body-s"
              color="text-body-secondary"
            >
              {`Show ${numberOfDuplicateToAdd} more`}
            </Box>
          </Box>
        </div>
      ) : null;
    return (
      <>
        <Box
          className="cr-creation-duplicate-title"
          padding={{ left: 'm', top: 'xs', bottom: 'xs' }}
        >
          <Icon variant="error" name="status-warning" />
          <Box variant="p" display="inline" fontSize="body-s" fontWeight="bold">
            {duplicateContentInfo}
          </Box>
          <Box fontSize="body-s" padding={{ left: 'l' }}>
            {constants.CONTENT_REQUEST_CREATION_DUPLICATE_CONTENT_MESSAGE}
          </Box>
        </Box>
        <div>
          <Table
            loadingText="searching duplicate content"
            columnDefinitions={columnDefinitions}
            className="cr-creation-duplicate-content-table"
            items={duplicateContentToShow}
          />
          {showMoreDuplicate}
        </div>
      </>
    );
  };

  const renderCodeIntegration = () => {
    if (contentType.label !== ContentType.Pattern) {
      return null;
    }
    return (
      <>
        <div className="cr-creation-border-bottom" />
        <Checkbox onChange={({ detail }) => setHasCode(detail.checked)} checked={requireCode}>
          {constants.CONTENT_REQUEST_CREATION_CODE_INTEGRATION_TEXT}
        </Checkbox>
      </>
    );
  };

  const showMoreDuplicateContent = () => {
    setNumberOfDuplicateToShow(numberOfDuplicateToShow + NUMBER_OF_DUPLICATE_CONTENT_TO_ADD);
  };

  const validContentRequestInput = () => {
    const errorMessage = '';
    if (errorMessage !== '') {
      setCreationError(errorMessage);
      setIsInProgress(false);
      return false;
    }
    return true;
  };

  const handleSuggestionSubmit = async () => {
    // prevents submission if either title or description are invalid or if the search for a duplicate title is still ongoing
    if (!validateInput(title, description) || isDuplicateSearchState) {
      // the fields that would fail validation are at the top of the page
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
      return;
    }
    setIsInProgress(true);

    const tags: Array<{ key: string; value: string }> = [];
    Object.values(ContentRequestTagKey).forEach((tagKey) => {
      // @ts-expect-error TS2551 - strict migration
      if (selectedTags[tagKey]) {
        // @ts-expect-error TS2551 - strict migration
        tags.push({ key: tagKey, value: selectedTags[tagKey].label });
      }
    });
    const contentRequestData = {
      contentName: title,
      description,
      requireCode,
      tags,
    };

    // Validate content name and description inputs
    if (!validContentRequestInput()) {
      return;
    }

    try {
      await mplServiceClient.createContentRequest(
        contentType.label as ContentType,
        contentRequestData,
        assigneeId,
      );
      BrowserHistory.push(`/`);
    } catch (e) {
      // @ts-expect-error TS18046 - strict migration
      setCreationError(e.message);
    } finally {
      setIsInProgress(false);
    }
  };

  const cancelSuggestionCreation = () => {
    BrowserHistory.push(`/`);
  };

  const renderAssigneeSection = () => {
    if (isAssigneeDisabled) {
      return null;
    }
    return (
      <Container
        header={
          <Header variant="h3" description="Select an author">
            Assign to author
          </Header>
        }
      >
        <FormField label="Find author by name" i18nStrings={{ errorIconAriaLabel: 'Error' }}>
          <UserIdAutosuggest onUserSelected={handleAssigneeSelected} />
        </FormField>
      </Container>
    );
  };

  const titleEditor = (
    <EditorWrapper
      model={title}
      readOnly={false}
      onModelChange={handleTitleChange}
      menuType={TiptapMenuType.Hide}
    />
  );

  // TODO: add placeholder
  const descriptionEditor = (
    <EditorWrapper
      model={description}
      readOnly={false}
      onModelChange={handleDescriptionChange}
      menuType={TiptapMenuType.Hide}
    />
  );

  return (
    <Box variant="div" margin={{ left: 'xl' }}>
      <div className="cr-creatoin-left-panel">
        <SpaceBetween size="s">
          <ContentRequestCreationHeader />
          <Container
            header={
              <Header variant="h3" description={constants.CONTENT_REQUEST_CREATION_TITLE_DESC}>
                {constants.CONTENT_REQUEST_CREATION_TITLE}
              </Header>
            }
          >
            <FormField errorText={titleErrorText} i18nStrings={{ errorIconAriaLabel: 'Error' }}>
              <SpaceBetween size="s">
                {titleEditor}
                {renderDuplicates()}
              </SpaceBetween>
            </FormField>
          </Container>
          <Container
            header={
              <Header
                variant="h3"
                description={constants.CONTENT_REQUEST_CREATION_DESCRIPTION_DESC}
              >
                {constants.CONTENT_REQUEST_CREATION_DESCRIPTION_HEADER}
              </Header>
            }
          >
            <FormField
              errorText={descriptionErrorText}
              i18nStrings={{ errorIconAriaLabel: 'Error' }}
            >
              {descriptionEditor}
              {renderDescriptionMessage()}
            </FormField>
          </Container>
          <Container
            header={
              <Header variant="h3">
                {constants.CONTENT_REQUEST_CREATION_CONFIGURATION_HEADER}
              </Header>
            }
          >
            <FormField
              label={constants.CONTENT_REQUEST_CREATION_CONFIGURATION_HEADER}
              i18nStrings={{ errorIconAriaLabel: 'Error' }}
            >
              {renderContentTypeSelector()}
              <div className="cr-creation-border-bottom" />
              {renderTagSelections()}
              {renderCodeIntegration()}
            </FormField>
          </Container>

          {renderAssigneeSection()}
          {renderCreationError()}
          {renderActionButton()}
        </SpaceBetween>
      </div>
    </Box>
  );
};

export default withAppContext(ContentRequestCreation);
